import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createCommunitiesApi } from "../api";
import {
  allowedCommunityActions,
  allowedFeatureActions,
  allowedMembershipActions,
  allowedOwnershipActions,
  allowedPostActions,
  communityStatusTone,
  featureStatusTone,
  formatLabel,
  healthStatusTone,
  membershipStatusTone,
  ownershipStatusTone,
  postStatusTone,
  visibilityStatusTone,
} from "../labels";
import { CommunityOverviewPanel } from "../components/CommunityOverviewPanel";
import type {
  CommunityDto,
  CommunityOverview,
  CommunityFeatureAction,
  CommunityMembershipAction,
  CommunityOwnershipAction,
  FeatureDto,
  HealthSnapshotDto,
  MembershipDto,
  OwnershipDto,
  PostAction,
  PostDto,
} from "../types";
import {
  AttachFileDialog,
  CommunityActionDialog,
  CreateFeatureDialog,
  CreateHealthSnapshotDialog,
  CreateMembershipDialog,
  CreateOwnershipDialog,
  CreatePostDialog,
  EditCommunityDrawer,
  FeatureActionDialog,
  PostActionDialog,
  ReasonActionDialog,
  TransferOwnershipDialog,
} from "../components/CommunityDialogs";

export function CommunityDetailPage() {
  const { communityId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createCommunitiesApi(client), [client]);
  const toast = useToast();

  const [community, setCommunity] = useState<CommunityDto | null>(null);
  const [memberships, setMemberships] = useState<MembershipDto[]>([]);
  const [ownerships, setOwnerships] = useState<OwnershipDto[]>([]);
  const [posts, setPosts] = useState<PostDto[]>([]);
  const [snapshots, setSnapshots] = useState<HealthSnapshotDto[]>([]);
  const [features, setFeatures] = useState<FeatureDto[]>([]);
  const [nestedError, setNestedError] = useState<string | null>(null);
  const [overview, setOverview] = useState<CommunityOverview | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<{ userId: string; label: string } | null>(null);
  const [membershipAction, setMembershipAction] = useState<{
    id: string;
    action: CommunityMembershipAction;
  } | null>(null);
  const [ownershipAction, setOwnershipAction] = useState<{
    id: string;
    action: Exclude<CommunityOwnershipAction, "transfer">;
  } | null>(null);
  const [transferOwnershipId, setTransferOwnershipId] = useState<string | null>(
    null,
  );
  const [postAction, setPostAction] = useState<{
    id: string;
    action: PostAction;
  } | null>(null);
  const [featureAction, setFeatureAction] = useState<{
    id: string;
    action: CommunityFeatureAction;
  } | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Communities", to: "/app/communities" },
    { label: community?.name ?? community?.display_code ?? "Community Detail" },
  ]);

  const load = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError(null);
    setNestedError(null);
    const [detailResult, membershipResult, ownershipResult, postResult, snapshotResult, featureResult, overviewResult] =
      await Promise.allSettled([
        api.getDetail(communityId),
        api.listMemberships(communityId, { limit: 50 }),
        api.listOwnerships(communityId, { limit: 50 }),
        api.listPosts(communityId, { limit: 50 }),
        api.listHealthSnapshots(communityId, {
          limit: 50,
          sort: "-measured_at",
        }),
        api.listFeatures(communityId, { limit: 50 }),
        api.overview(communityId),
      ]);
    if (overviewResult.status === "fulfilled") {
      setOverview(overviewResult.value.overview);
      setOverviewError(null);
    } else {
      setOverview(null);
      setOverviewError(apiErrorMessage(overviewResult.reason));
    }
    if (detailResult.status === "fulfilled") {
      setCommunity(detailResult.value.community);
      setError(null);
    } else {
      setCommunity(null);
      setError(apiErrorMessage(detailResult.reason));
    }
    const failures: string[] = [];
    if (membershipResult.status === "fulfilled") {
      setMemberships(membershipResult.value.memberships);
    } else {
      setMemberships([]);
      failures.push("memberships");
    }
    if (ownershipResult.status === "fulfilled") {
      setOwnerships(ownershipResult.value.ownerships);
    } else {
      setOwnerships([]);
      failures.push("ownership");
    }
    if (postResult.status === "fulfilled") {
      setPosts(postResult.value.posts);
    } else {
      setPosts([]);
      failures.push("posts");
    }
    if (snapshotResult.status === "fulfilled") {
      setSnapshots(snapshotResult.value.health_snapshots);
    } else {
      setSnapshots([]);
      failures.push("health snapshots");
    }
    if (featureResult.status === "fulfilled") {
      setFeatures(featureResult.value.features);
    } else {
      setFeatures([]);
      failures.push("features");
    }
    if (failures.length > 0) {
      setNestedError(`Unable to load ${failures.join(", ")}.`);
    }
    setLoading(false);
  }, [api, communityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const communityActions = useMemo(
    () => (community ? allowedCommunityActions(community.status) : []),
    [community],
  );

  async function run(label: string, work: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      toast.push({ title: label, tone: "success" });
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <LoadingState message="Loading Community Detail…" />;
  }

  if (error || !community) {
    return (
      <EmptyState
        title="Community unavailable"
        description={error ?? "Community not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={community.name}
        description={community.display_code ? `Code ${community.display_code}` : undefined}
        actions={
          <Flex gap={8} wrap>
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              Edit profile
            </Button>
            <Button
              variant="secondary"
              disabled={communityActions.length === 0}
              onClick={() => setStatusOpen(true)}
            >
              Status actions
            </Button>
            <Button variant="secondary" onClick={() => setAttachOpen(true)}>
              Attach file
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} align="center" wrap>
            <StatusChip tone={communityStatusTone(community.status)}>
              Status: {formatLabel(community.status)}
            </StatusChip>
            <StatusChip
              tone={visibilityStatusTone(community.visibility_status)}
            >
              Visibility: {formatLabel(community.visibility_status)}
            </StatusChip>
            <StatusChip tone={healthStatusTone(community.health_status)}>
              Health:{" "}
              {community.health_status
                ? formatLabel(community.health_status)
                : "no snapshot"}
            </StatusChip>
          </Flex>
        }
      />

      {actionError ? (
        <Alert tone="danger" title="Action failed">
          {actionError}
        </Alert>
      ) : null}
      {nestedError ? (
        <Alert tone="warning" title="Some nested lists failed">
          {nestedError}
        </Alert>
      ) : null}

      {overviewError ? (
        <Alert tone="warning" title="The community's app-side details could not be loaded">
          {overviewError}
        </Alert>
      ) : null}
      {overview ? (
        <CommunityOverviewPanel
          overview={overview}
          {...(overview.source !== "hq" ? { onRemove: setRemoveTarget } : {})}
        />
      ) : null}

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Identity
          </Text>
          <Text role="body" as="p">
            Category: {community.category ?? "—"}
          </Text>
          <Text role="body" as="p">
            Market: {community.market_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Description: {community.description ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Community ID: {community.id}. Close and archive retain memberships,
            ownership, posts, and history. Current health is a summary of the
            latest snapshot.
          </Text>
          <Text role="caption" color="muted" as="p">
            Reports against this community are listed above and are worked in
            Trust & Safety. Status actions change the community in the app:
            Restrict stops new posts and members, Pause hides it and makes it
            read-only, Close archives it for good.
          </Text>
        </Stack>
      </Card>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Memberships
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setMembershipOpen(true)}
            >
              Add membership
            </Button>
          </Flex>
          <Text role="small" color="secondary" as="p">
            Independent of Ownership, Business Relationship, and Creator
            Program Membership.
          </Text>
          {memberships.length === 0 ? (
            <EmptyState title="No memberships" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Joined</Th>
                  <Th>Ended</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {memberships.map((row) => (
                  <Tr key={row.id}>
                    <Td>{row.user_id}</Td>
                    <Td>{formatLabel(row.membership_type)}</Td>
                    <Td>
                      <StatusChip tone={membershipStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>{row.joined_at ?? "—"}</Td>
                    <Td>{row.ended_at ?? "—"}</Td>
                    <Td>
                      <Flex gap={4} wrap>
                        {allowedMembershipActions(row.status)
                          // In an app community only Remove reaches players (i139).
                          .filter((action) => overview?.source === "hq" || action === "remove")
                          .map((action) => (
                          <Button
                            key={action}
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              setMembershipAction({ id: row.id, action })
                            }
                          >
                            {formatLabel(action)}
                          </Button>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Ownership
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setOwnershipOpen(true)}
            >
              Propose ownership
            </Button>
          </Flex>
          <Text role="small" color="secondary" as="p">
            Founder, owner, and manager are distinct. Transfer requires Manager
            Approval.
          </Text>
          {ownerships.length === 0 ? (
            <EmptyState title="No ownership records" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Prior user</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {ownerships.map((row) => (
                  <Tr key={row.id}>
                    <Td>{row.user_id}</Td>
                    <Td>{formatLabel(row.ownership_type)}</Td>
                    <Td>
                      <StatusChip tone={ownershipStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>{row.effective_from}</Td>
                    <Td>{row.effective_to ?? "—"}</Td>
                    <Td>{row.transferred_from_user_id ?? "—"}</Td>
                    <Td>
                      <Flex gap={4} wrap>
                        {allowedOwnershipActions(row.status).map((action) => (
                          <Button
                            key={action}
                            size="sm"
                            variant={
                              action === "transfer" || action === "revoke"
                                ? "danger"
                                : "secondary"
                            }
                            onClick={() => {
                              if (action === "transfer") {
                                setTransferOwnershipId(row.id);
                              } else {
                                setOwnershipAction({ id: row.id, action });
                              }
                            }}
                          >
                            {formatLabel(action)}
                          </Button>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Posts
            </Text>
            <Button size="sm" variant="secondary" onClick={() => setPostOpen(true)}>
              Create post
            </Button>
          </Flex>
          {posts.length === 0 ? (
            <EmptyState title="No posts" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Author</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Reference</Th>
                  <Th>Published</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {posts.map((row) => (
                  <Tr key={row.id}>
                    <Td>{row.author_user_id}</Td>
                    <Td>{formatLabel(row.content_type)}</Td>
                    <Td>
                      <StatusChip tone={postStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>{row.content_reference}</Td>
                    <Td>{row.published_at ?? "—"}</Td>
                    <Td>
                      <Flex gap={4} wrap>
                        {allowedPostActions(row.status).map((action) => (
                          <Button
                            key={action}
                            size="sm"
                            variant="secondary"
                            onClick={() => setPostAction({ id: row.id, action })}
                          >
                            {formatLabel(action)}
                          </Button>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Health snapshots
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSnapshotOpen(true)}
            >
              Record snapshot
            </Button>
          </Flex>
          <Text role="small" color="secondary" as="p">
            Append-only. Do not treat current health_status as a replacement
            for this history.
          </Text>
          {snapshots.length === 0 ? (
            <EmptyState title="No health snapshots" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Measured</Th>
                  <Th>Risk summary</Th>
                  <Th>Reviewed by</Th>
                </Tr>
              </Thead>
              <Tbody>
                {snapshots.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <StatusChip tone={healthStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>{row.measured_at}</Td>
                    <Td>{row.risk_summary ?? "—"}</Td>
                    <Td>{row.reviewed_by_user_id ?? "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Feature placement
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFeatureOpen(true)}
            >
              Propose feature
            </Button>
          </Flex>
          <Text role="small" color="secondary" as="p">
            Approve requires Manager Approval. Marketing never sees private
            moderation evidence. This is not a commercial payment workflow.
          </Text>
          {features.length === 0 ? (
            <EmptyState title="No feature placements" reason="none" />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Campaign</Th>
                  <Th>Starts</Th>
                  <Th>Ends</Th>
                  <Th>Approved by</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {features.map((row) => (
                  <Tr key={row.id}>
                    <Td>
                      <StatusChip tone={featureStatusTone(row.status)}>
                        {formatLabel(row.status)}
                      </StatusChip>
                    </Td>
                    <Td>{row.creator_campaign_id ?? "—"}</Td>
                    <Td>{row.starts_at ?? "—"}</Td>
                    <Td>{row.ends_at ?? "—"}</Td>
                    <Td>{row.approved_by_user_id ?? "—"}</Td>
                    <Td>
                      <Flex gap={4} wrap>
                        {allowedFeatureActions(row.status).map((action) => (
                          <Button
                            key={action}
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              setFeatureAction({ id: row.id, action })
                            }
                          >
                            {formatLabel(action)}
                          </Button>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>

      <EditCommunityDrawer
        open={editOpen}
        name={community.name}
        marketId={community.market_id ?? ""}
        description={community.description ?? ""}
        category={community.category ?? ""}
        visibilityStatus={community.visibility_status}
        busy={busy}
        error={actionError}
        onClose={() => {
          setEditOpen(false);
          setActionError(null);
        }}
        onSubmit={async (patch) => {
          await run("Community updated", async () => {
            await api.updateProfile(community.id, patch);
            setEditOpen(false);
          });
        }}
      />

      <CommunityActionDialog
        open={statusOpen}
        currentStatus={community.status}
        actions={communityActions}
        busy={busy}
        error={actionError}
        onClose={() => {
          setStatusOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run(`Community ${formatLabel(input.action)}`, async () => {
            await api.applyAction(community.id, input.action, {
              reason: input.reason,
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
              ...(input.high_impact !== undefined
                ? { high_impact: input.high_impact }
                : {}),
            });
            setStatusOpen(false);
          });
        }}
      />

      <AttachFileDialog
        open={attachOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setAttachOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("File attached", async () => {
            await api.attachFile(community.id, input);
            setAttachOpen(false);
          });
        }}
      />

      <CreateMembershipDialog
        open={membershipOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setMembershipOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Membership created", async () => {
            await api.createMembership(community.id, input);
            setMembershipOpen(false);
          });
        }}
      />

      <ReasonActionDialog
        open={removeTarget !== null}
        title={removeTarget ? `Remove ${removeTarget.label}` : "Remove from community"}
        notice="They are taken out of the community in the app straight away and get a notice from HUDDL that includes this reason. Nothing stops them asking to join again."
        confirmLabel="Remove"
        busy={busy}
        error={actionError}
        onClose={() => {
          setRemoveTarget(null);
          setActionError(null);
        }}
        onSubmit={async (reason) => {
          if (!removeTarget) return;
          const target = removeTarget;
          await run(`${target.label} removed`, async () => {
            await api.removeMember(community.id, target.userId, reason);
            setRemoveTarget(null);
          });
        }}
      />

      <ReasonActionDialog
        open={membershipAction !== null}
        title={
          membershipAction
            ? `Membership ${formatLabel(membershipAction.action)}`
            : "Membership action"
        }
        busy={busy}
        error={actionError}
        onClose={() => {
          setMembershipAction(null);
          setActionError(null);
        }}
        onSubmit={async (reason) => {
          if (!membershipAction) return;
          const current = membershipAction;
          await run(`Membership ${formatLabel(current.action)}`, async () => {
            await api.applyMembershipAction(current.id, current.action, {
              reason,
            });
            setMembershipAction(null);
          });
        }}
      />

      <CreateOwnershipDialog
        open={ownershipOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setOwnershipOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Ownership created", async () => {
            await api.createOwnership(community.id, input);
            setOwnershipOpen(false);
          });
        }}
      />

      <ReasonActionDialog
        open={ownershipAction !== null}
        title={
          ownershipAction
            ? `Ownership ${formatLabel(ownershipAction.action)}`
            : "Ownership action"
        }
        busy={busy}
        error={actionError}
        onClose={() => {
          setOwnershipAction(null);
          setActionError(null);
        }}
        onSubmit={async (reason) => {
          if (!ownershipAction) return;
          const current = ownershipAction;
          await run(`Ownership ${formatLabel(current.action)}`, async () => {
            await api.applyOwnershipAction(current.id, current.action, {
              reason,
            });
            setOwnershipAction(null);
          });
        }}
      />

      <TransferOwnershipDialog
        open={transferOwnershipId !== null}
        busy={busy}
        error={actionError}
        onClose={() => {
          setTransferOwnershipId(null);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          if (!transferOwnershipId) return;
          const ownershipId = transferOwnershipId;
          await run("Ownership transferred", async () => {
            await api.applyOwnershipAction(ownershipId, "transfer", input);
            setTransferOwnershipId(null);
          });
        }}
      />

      <CreatePostDialog
        open={postOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setPostOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Post created", async () => {
            await api.createPost(community.id, input);
            setPostOpen(false);
          });
        }}
      />

      <PostActionDialog
        open={postAction !== null}
        action={postAction?.action ?? null}
        busy={busy}
        error={actionError}
        onClose={() => {
          setPostAction(null);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          if (!postAction) return;
          const current = postAction;
          await run(`Post ${formatLabel(current.action)}`, async () => {
            await api.applyPostAction(current.id, current.action, input);
            setPostAction(null);
          });
        }}
      />

      <CreateHealthSnapshotDialog
        open={snapshotOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setSnapshotOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Health snapshot recorded", async () => {
            await api.createHealthSnapshot(community.id, input);
            setSnapshotOpen(false);
          });
        }}
      />

      <CreateFeatureDialog
        open={featureOpen}
        busy={busy}
        error={actionError}
        onClose={() => {
          setFeatureOpen(false);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          await run("Feature proposed", async () => {
            await api.createFeature(community.id, input);
            setFeatureOpen(false);
          });
        }}
      />

      <FeatureActionDialog
        open={featureAction !== null}
        action={featureAction?.action ?? null}
        busy={busy}
        error={actionError}
        onClose={() => {
          setFeatureAction(null);
          setActionError(null);
        }}
        onSubmit={async (input) => {
          if (!featureAction) return;
          const current = featureAction;
          await run(`Feature ${formatLabel(current.action)}`, async () => {
            await api.applyFeatureAction(current.id, current.action, input);
            setFeatureAction(null);
          });
        }}
      />
    </Stack>
  );
}
